<template>
  <div class="tender-title" :class="{ 'is-small': isSmall }">
    <div class="tender-title__infos" :class="{ 'tender-title__infos--with-related': relatedSources.length }">
      <div class="tender-title__score-status">
        <span v-if="tenantHasTenderScoringAccess()" class="tender-title__score-section">
          <span class="tender-title__subtitle"> {{ $t('search-filters.relevance-score.name') }}:</span>
          <BaseIcon
            v-tooltip="{ content: $t('search-filters.relevance-score.tooltip'), placement: 'top' }"
            name="info"
            class="tender-title__score-info"
            role="img"
            tabindex="0"
            :aria-label="$t('search-filters.relevance-score.tooltip')"
          />
          <span v-if="tender.score !== null">
            <DynamicThermometer :value="tender.score" />
          </span>
          <span v-else class="tender-title__empty-score">N/A</span>
        </span>
      </div>

      <TenderStatus class="tender-title__item" :status="tender.status" />
      <TenderTimeLeft
        class="tender-title__item"
        :status="tender.status"
        :daysBeforeClosing="tender.daysBeforeClosing"
      />

      <div
        class="tender-title__item"
        :class="{ 'tender-title__item--before-related': relatedSources.length }"
        v-on:click="goToSource"
      >
        <span class="tender-title__subtitle">{{ $t('tender-details.origin') }}:</span>
        <!-- This component's id is used by our chrome extension -->
        <BaseLink
          v-if="tender.source.id"
          target-blank
          :href="tender.source.url"
          id="tender-source-url"
          v-tooltip="{ content: $t('tender-details.origin-site-access'), placement: 'top', offset: '8px' }"
        >
          <PlatformIcon :platform="tender.source.id" class="tender-title__platform-icon" />
          {{ platformName }}
        </BaseLink>
        <span class="tender-title__separator"></span>
        <BaseTag tone="neutral" preset="soft">
          {{ referenceNumber }}
        </BaseTag>
      </div>

      <div v-if="relatedSources.length" class="tender-title__item tender-title__item--related">
        <span class="tender-title__subtitle">{{ $t('tender-details.related-sources') }}:</span>
        <TenderRelatedSources :related-sources="relatedSources" separated />
      </div>
    </div>

    <div class="tender-title__title" :class="{ 'is-small': isSmall }">
      <TextHighlight :content="title"></TextHighlight>
    </div>
    <div v-if="tender.tenantTender && tender.tenantTender.archived" class="tender-title__archived-by">
      {{ archivedBy }}
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseLink from '@/components/base/BaseLink';
import BaseTag from '@/components/base/BaseTag.vue';
import PlatformName from '@/mixins/platform-name-mixin';
import PlatformIcon from '@/components/PlatformIcon';
import TenderRelatedSources from '@/components/page-tender-details/TenderRelatedSources.vue';
import TenderStatus from '@/components/page-tender-details/TenderStatus';
import TenderTimeLeft from '@/components/page-tender-details/TenderTimeLeft.vue';
import TextHighlight from '@/components/text-highlight/TextHighlight';
import DynamicThermometer from '@/components/util/DynamicThermometer';
import { tenantHasTenderScoringAccess } from '@/util/module-access-util';
import { sameTenderSources } from '@/util/related-sources-util';

import { mapGetters } from 'vuex';
import { formatShortDate, toMoment } from '@/util/date-util';

export default {
  name: 'TenderTitle',
  components: {
    BaseIcon,
    BaseLink,
    BaseTag,
    PlatformIcon,
    TenderRelatedSources,
    TenderStatus,
    TenderTimeLeft,
    TextHighlight,
    DynamicThermometer,
  },
  mixins: [PlatformName],
  props: {
    tender: {
      type: Object,
      required: true,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('tenantPlatforms', ['mergedPlatforms']),
    ...mapGetters('tenant', ['users']),
    title() {
      return this.tender.title ? this.tender.title : '';
    },
    platformName() {
      return this.getPlatformName(this.tender.source.id);
    },
    relatedSources() {
      return sameTenderSources(this.tender);
    },
    referenceNumber() {
      return this.tender.source && this.tender.source.referenceNumber
        ? this.$t('tender-details.reference-num', { number: this.tender.source.referenceNumber })
        : this.$t('basic-words.none');
    },
    archivedBy() {
      if (this.tender.tenantTender && this.tender.tenantTender.archived) {
        const user = this.users.find((user) => user.id === this.tender.tenantTender.archivedBy);
        if (user) {
          return this.$t('tender-details.archived-by', {
            name: user.fullName,
            date: formatShortDate(toMoment(this.tender.tenantTender.archivedDate)),
          });
        }
        return this.$t(
          this.tender.tenantTender.archivedBy ? 'tender-details.archived-by-former-user' : 'tender-details.archived-on',
          { date: formatShortDate(toMoment(this.tender.tenantTender.archivedDate)) },
        );
      }
      return '';
    },
  },
  methods: {
    tenantHasTenderScoringAccess,
    goToSource() {
      this.$track.tenderExternalLink(this.tender.source.id);
    },
  },
};
</script>

<style lang="scss">
.tender-title {
  margin-bottom: 16px;
  background: $color-white;
  box-shadow: $shadow-default;
  padding: 2rem;
  border-radius: $border-radius-root;

  &.is-small {
    box-shadow: none;
    padding: 0 16px;
    margin-bottom: 0px;
    align-self: center;
  }

  &__infos {
    display: flex;
    width: 100%;
    align-items: center;

    // Only the related sources may take a line of their own: without them, the row squeezes as it
    // always has (a long reference number goes under its platform).
    &--with-related {
      flex-wrap: wrap;
      row-gap: 8px;
    }
  }

  &__item {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    padding: 0 1rem;

    &:first-child {
      padding-left: 0;
    }

    // Beside Provenance when it fits, else on a line of its own, under Score: the gap between them
    // is Provenance's, so a wrapped row starts flush. Too wide for a whole line, its sources wrap
    // beside the label.
    &--related {
      flex: 0 1 auto;
      flex-wrap: nowrap;
      min-width: 0;
      padding-left: 0;

      .tender-title__subtitle {
        flex-shrink: 0;
      }
    }

    &--before-related {
      padding-right: 2rem;
    }
  }

  &__title {
    font-family: $font-family-raleway;
    font-size: 2.5rem;
    font-weight: bold;
    margin-top: 1rem;

    &.is-small {
      font-size: 1rem;
      margin-top: 4px;
    }
  }

  &__subtitle {
    color: $color-normal;
    font-size: $font-size-root;
    font-weight: bold;
    padding-right: 0.5rem;
  }

  &__platform-icon {
    padding-right: 8px;
  }

  &__separator {
    background: $color-border;
    display: block;
    height: 24px;
    margin: 0 8px;
    width: 1px;
  }

  &__archived-by {
    font-family: Lato;
    font-size: $font-size-small;
    font-weight: 600;
    color: $color-dark-blue-pale;
    margin-top: 0.56rem;
  }

  &__score-section {
    display: flex;
    gap: 4px;
    align-items: center;
    margin-right: 16px;
    position: relative;
  }

  &__score-info {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: $color-normal-pale;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $color-accent;
      outline-offset: 2px;
      border-radius: 50%;
    }
  }

  &__score {
    display: flex;
    font-weight: normal;
    gap: 4px;
    align-items: center;
  }

  &__empty-score {
    font-size: 14px;
    color: $color-neutral;
    align-items: center;
    display: flex;
    margin-top: 4px;
  }

  @media screen and (max-width: 1080px) {
    &__infos {
      display: block;
    }

    &__item {
      border-right: none;
      padding: 8px 0px;
      width: 100%;
      height: auto;
      align-content: center;
    }

    &__subtitle {
      width: 116px;
    }
  }

  @media screen and (max-width: 784px) {
    &__title {
      font-size: 30px;
    }
  }
}
</style>
